import { lazy, Suspense, useCallback, useEffect, useState } from 'react';

import { useSession } from '../../features/auth/hooks/use-session.js';
import { useFood } from '../../features/food/hooks/use-food.js';
import { useCalendar } from '../../features/calendar/hooks/use-calendar.js';
import { useReminders } from '../../features/reminders/hooks/use-reminders.js';
import { useProfile } from '../../features/profile/hooks/use-profile.js';
import * as accountTools from '../../features/account/api/account-tools.api.js';
import * as integrationsApi from '../../features/integrations/api/integrations.api.js';
import { EmptyState } from '../../ui/EmptyState.jsx';
import { ErrorBoundary } from '../../ui/ErrorBoundary.jsx';
import { Skeleton } from '../../ui/Skeleton.jsx';
import { Modal } from '../../ui/Modal.jsx';
import { useAppDateKey } from '../hooks/use-app-date-key.js';
import { HeaderBar } from './HeaderBar.jsx';
import { QuickNav } from './QuickNav.jsx';
import { scrollToSection } from './scroll-to-section.js';
import {
  APP_SECTIONS,
  DAY_ROLLOVER_CHECK_INTERVAL_MS,
  HANDOFF_FOOTNOTE,
  INTERIM_AVATAR_URL,
} from './shell.constants.js';

const imports = {
  account: () => import('../../features/account/components/AccountPanel.jsx'),
  calories: () => import('../../features/nutrition/components/CaloriesPanel.jsx'),
  health: () => import('../../features/health/components/HealthPanel.jsx'),
  training: () => import('../../features/training/components/TrainingPanel.jsx'),
  nearby: () => import('../../features/nearby/components/NearbyPanel.jsx'),
  food: () => import('../../features/food/components/FoodLogModal.jsx'),
  calendar: () => import('../../features/calendar/components/CalendarModal.jsx'),
  masterDay: () => import('../../features/calendar/components/MasterDayPanel.jsx'),
  reminderOverlay: () => import('../../features/reminders/components/ReminderOverlay.jsx'),
  reminders: () => import('../../features/reminders/components/RemindersHub.jsx'),
};
const AccountPanel = lazy(() => imports.account().then((module) => ({ default: module.AccountPanel })));
const CaloriesPanel = lazy(() => imports.calories().then((module) => ({ default: module.CaloriesPanel })));
const HealthPanel = lazy(() => imports.health().then((module) => ({ default: module.HealthPanel })));
const TrainingPanel = lazy(() => imports.training().then((module) => ({ default: module.TrainingPanel })));
const NearbyPanel = lazy(() => imports.nearby().then((module) => ({ default: module.NearbyPanel })));
const FoodLogModal = lazy(() => imports.food().then((module) => ({ default: module.FoodLogModal })));
const CalendarModal = lazy(() => imports.calendar().then((module) => ({ default: module.CalendarModal })));
const MasterDayPanel = lazy(() => imports.masterDay().then((module) => ({ default: module.MasterDayPanel })));
const ReminderOverlay = lazy(() => imports.reminderOverlay().then((module) => ({ default: module.ReminderOverlay })));
const RemindersHub = lazy(() => imports.reminders().then((module) => ({ default: module.RemindersHub })));
const FeatureFallback = () => <div className="card feature-data-loading" role="status"><p>Loading data from the database…</p><Skeleton lines={3} label={null} /></div>;
const PopupFallback = ({ title, onClose, drawer = false }) => <Modal title={title} onClose={onClose} maxWidth={drawer ? 520 : 760} variant={drawer ? 'drawer' : 'modal'} className={drawer ? 'account-modal' : ''} scrimClassName={drawer ? 'account-drawer-scrim' : ''}><Skeleton lines={5} label={`Loading ${title}`} /></Modal>;

/**
 * The outer layout: header, sticky quick nav, section frame, footnote.
 *
 * Every section is wrapped in its own error boundary, so one feature slice failing to
 * render leaves the rest of the dashboard usable.
 *
 * Section bodies are placeholders until each slice lands. Nothing here renders a number,
 * a total or a status, because a placeholder that looks like real data is worse than one
 * that says what it is.
 */
export function AppShell() {
  const dateKey = useAppDateKey(DAY_ROLLOVER_CHECK_INTERVAL_MS);
  const { user } = useSession();
  const { profile, save: saveProfile } = useProfile();
  const [accountOpen, setAccountOpen] = useState(null);
  const [foodView, setFoodView] = useState(null);
  const [healthOpenRequest, setHealthOpenRequest] = useState(null);
  const [hydrationOpenRequest, setHydrationOpenRequest] = useState(null);
  const [foodSyncVersion, setFoodSyncVersion] = useState(0);
  const [masterOpen, setMasterOpen] = useState(false);
  const [masterSource, setMasterSource] = useState('master');
  const [masterMonth, setMasterMonth] = useState(dateKey.slice(0, 7));
  const [calendarRevision, setCalendarRevision] = useState(0);
  const [integrationRevision, setIntegrationRevision] = useState(0);
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState('loading');
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(INTERIM_AVATAR_URL);
  const foodDateKey = foodView?.dateKey ?? dateKey;
  const food = useFood(foodDateKey, { active: foodView !== null });
  const calendar = useCalendar(masterMonth, { active: masterOpen });
  const reminders = useReminders(dateKey);

  useEffect(() => {
    const controller = new AbortController();
    setGoogleCalendarStatus('loading');
    void integrationsApi.fetchIntegrations({ signal: controller.signal }).then((result) => {
      const google = result.integrations.find((item) => item.provider === 'google_calendar');
      setGoogleCalendarStatus(google?.connected ? 'connected' : google?.configured ? 'disconnected' : 'unavailable');
    }).catch(() => { if (!controller.signal.aborted) setGoogleCalendarStatus('disconnected'); });
    return () => controller.abort();
  }, [integrationRevision]);

  useEffect(() => {
    const preload = () => { void imports.account(); void imports.food(); void imports.calendar(); void imports.reminders(); };
    if ('requestIdleCallback' in globalThis) {
      const id = globalThis.requestIdleCallback(preload, { timeout: 1500 });
      return () => globalThis.cancelIdleCallback?.(id);
    }
    const id = globalThis.setTimeout(preload, 250);
    return () => globalThis.clearTimeout(id);
  }, []);

  useEffect(() => {
    let active = true;
    void accountTools.listMedia().then((result) => {
      const avatar = result.assets?.find((asset) => asset.category === 'avatar');
      if (active && avatar?.url) setAvatarUrl(avatar.url);
    }).catch(() => { /* The bundled profile photo remains a safe fallback. */ });
    const changed = (event) => { if (event.detail?.url) setAvatarUrl(event.detail.url); };
    globalThis.addEventListener('eax-fuel:avatar-changed', changed);
    return () => { active = false; globalThis.removeEventListener('eax-fuel:avatar-changed', changed); };
  }, []);

  // Opening Master Calendar lands on the month grid, nothing pre-selected — it used to
  // jump straight to today's day-detail popup, which meant every open showed a popup the
  // user had not asked for before they had even seen the month. A day only opens once its
  // own cell is clicked, the same way it already worked for every other day in the grid.
  useEffect(() => { if (masterOpen) calendar.clearSelection(); }, [masterOpen, calendar.clearSelection]);

  const openMasterCalendar = useCallback((selectedDateKey = dateKey, source = 'master') => {
    setMasterMonth(selectedDateKey.slice(0, 7));
    setMasterSource(source);
    setMasterOpen(true);
  }, [dateKey]);

  const closeMasterCalendar = useCallback(() => {
    calendar.clearSelection();
    setMasterOpen(false);
    setMasterSource('master');
    setCalendarRevision((value) => value + 1);
  }, [calendar.clearSelection]);

  const closeAccount = useCallback(() => {
    setAccountOpen(null);
    setIntegrationRevision((value) => value + 1);
  }, []);

  const openGoogleCalendarSettings = useCallback(() => {
    if (masterOpen) closeMasterCalendar();
    setAccountOpen('sync');
  }, [closeMasterCalendar, masterOpen]);

  const handleNavigate = useCallback((sectionId) => {
    if (sectionId === 'section-master-calendar') { openMasterCalendar(dateKey); return; }
    if (sectionId === 'section-food') { setFoodView({ tab: 'log', dateKey }); return; }
    if (sectionId === 'section-hydration') { scrollToSection('section-calories'); return; }
    scrollToSection(sectionId);
  }, [dateKey, openMasterCalendar]);

  // Where each reminder type's own "enter details" screen lives, so acting on a reminder
  // is one click to the right place rather than a trip through the dashboard to find it.
  const navigateToReminder = useCallback((type) => {
    const healthItemId = { vitamins: 'vitamins', bloodwork: 'bloodwork', weigh_in: 'weight', grocery: 'grocery' }[type];
    if (healthItemId) { scrollToSection('section-health'); setHealthOpenRequest({ id: healthItemId }); return; }
    if (type === 'pre_workout' || type === 'session_start' || type === 'post_workout') { scrollToSection('section-training'); return; }
    if (type === 'window_closing' || type === 'low_intake' || type === 'macro') { setFoodView({ tab: 'log', dateKey }); return; }
    scrollToSection('section-calories');
    setHydrationOpenRequest({});
  }, [dateKey]);

  return (
    <div className="app-shell">
      <div className="app-shell__inner">
        <HeaderBar
          dateKey={dateKey}
          onOpenReminders={() => setRemindersOpen(true)}
          onOpenAccount={() => setAccountOpen('profile')}
          displayName={user?.displayName ?? null}
          avatarUrl={avatarUrl}
        />

        <QuickNav onNavigate={handleNavigate} />

        <Suspense fallback={null}><ReminderOverlay reminders={reminders} onNavigate={navigateToReminder} /></Suspense>

        <main className="app-main">
          {APP_SECTIONS.filter((section) => section.presentation === 'panel').map((section) => (
            <section key={section.id} id={section.id} className="app-section">
              <h2 className="visually-hidden">{section.title}</h2>
              <ErrorBoundary label={section.title}><Suspense fallback={<FeatureFallback />}>
                {section.id === 'section-master-day' ? (
                  <MasterDayPanel dateKey={dateKey} revision={calendarRevision} onOpenCalendar={(selectedDateKey) => openMasterCalendar(selectedDateKey, 'master')}
                    onOpenTraining={() => scrollToSection('section-training')} onOpenNutrition={() => scrollToSection('section-calories')} onOpenHealth={() => scrollToSection('section-health')}
                    onOpenHealthItem={(id) => { scrollToSection('section-health'); setHealthOpenRequest({ id }); }} />
                ) : section.id === 'section-calories' ? (
                  <CaloriesPanel profile={profile} dateKey={dateKey} foodSyncVersion={foodSyncVersion} trainingRevision={calendarRevision} hydrationOpenRequest={hydrationOpenRequest} onSaveProfile={saveProfile} onOpenFood={(selectedDateKey) => setFoodView({ tab: 'log', dateKey: selectedDateKey })} onOpenFasting={(selectedDateKey) => setFoodView({ tab: 'fasting', dateKey: selectedDateKey })} onOpenTraining={() => scrollToSection('section-training')} onOpenWearables={() => setAccountOpen('sync')} />
                ) : section.id === 'section-health' ? (
                  <HealthPanel dateKey={dateKey} openRequest={healthOpenRequest} />
                ) : section.id === 'section-training' ? (
                  <TrainingPanel initialDateKey={dateKey} weightLb={profile.weightLb} calendarRevision={calendarRevision} googleCalendarStatus={googleCalendarStatus} onOpenGoogleSettings={openGoogleCalendarSettings} onTrainingChanged={() => setCalendarRevision((value) => value + 1)} />
                ) : section.id === 'section-nearby' ? (
                  <NearbyPanel />
                ) : (
                  <EmptyState
                    title={section.title}
                    description="Not built yet. This section is next in line."
                  />
                )}
              </Suspense></ErrorBoundary>
            </section>
          ))}
        </main>

        <p className="app-footnote">{HANDOFF_FOOTNOTE}</p>
      </div>

      {accountOpen ? (
        <ErrorBoundary label="Account">
          <Suspense fallback={<PopupFallback title="Account" drawer onClose={closeAccount} />}><AccountPanel initialTab={accountOpen} reminders={reminders} onClose={closeAccount} /></Suspense>
        </ErrorBoundary>
      ) : null}
      {foodView ? <ErrorBoundary label="Food log"><Suspense fallback={<PopupFallback title="Food log" onClose={() => setFoodView(null)} />}><FoodLogModal dateKey={foodDateKey} initialTab={foodView.tab} food={food} onClose={() => { setFoodView(null); setFoodSyncVersion((version) => version + 1); }} /></Suspense></ErrorBoundary> : null}
      {masterOpen ? <ErrorBoundary label="Master Calendar"><Suspense fallback={<PopupFallback title="Master Calendar" onClose={closeMasterCalendar} />}><CalendarModal monthKey={masterMonth} setMonthKey={setMasterMonth} todayDateKey={dateKey} calendar={calendar} source={masterSource} weightLb={profile.weightLb} displayName={user?.displayName ?? null} googleCalendarStatus={googleCalendarStatus} onOpenGoogleSettings={openGoogleCalendarSettings} onClose={closeMasterCalendar} /></Suspense></ErrorBoundary> : null}
      {remindersOpen ? <ErrorBoundary label="Reminders">{reminders.settings ? <Suspense fallback={<PopupFallback title="Reminders" onClose={() => setRemindersOpen(false)} />}><RemindersHub reminders={reminders} onClose={() => setRemindersOpen(false)} /></Suspense> : <Modal title="Reminders" onClose={() => setRemindersOpen(false)} maxWidth={620}>{reminders.status === 'error' ? <p className="auth-form__failure" role="alert">{reminders.error}</p> : <Skeleton lines={4} label="Loading reminder settings" />}</Modal>}</ErrorBoundary> : null}
    </div>
  );
}
